import React from 'react';

/**
 * Sidebar filter rail for the Users tab.
 *
 * Props:
 *   roleFilter      'all' | 'admin' | 'manager' | 'employee' | 'inactive'
 *   onRoleChange
 *   deptFilterIds   number[]  (empty = all)
 *   onDeptToggle    (deptId)
 *   departments     [{id, name}]
 *   roleCounts      {all, admin, manager, employee, inactive}
 *   deptCounts      { [deptId]: number }  per dept: active users (All users) or leavers (Leavers tab)
 *   onClearAll
 *   showRole        boolean   false on the Leavers tab, which replaces the Role filter
 */
export default function UserFilterRail({
  roleFilter,
  onRoleChange,
  deptFilterIds,
  onDeptToggle,
  departments,
  roleCounts,
  deptCounts = {},
  onClearAll,
  showRole = true,
}) {
  const roles = [
    ['all',      'All users',  roleCounts.all],
    ['admin',    'Admins',     roleCounts.admin],
    ['manager',  'Managers',   roleCounts.manager],
    ['employee', 'Employees',  roleCounts.employee],
    ['inactive', 'Inactive',   roleCounts.inactive],
  ];
  const anyFilterActive = (showRole && roleFilter !== 'all') || deptFilterIds.length > 0;

  return (
    <aside className="ut-rail" aria-label="Filter users">
      {showRole && (
        <div className="ut-rail-section">
          <div className="ut-rail-label">Role</div>
          <div className="ut-rail-options" role="radiogroup" aria-label="Role filter">
            {roles.map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={roleFilter === key}
                className={`ut-rail-option ${roleFilter === key ? 'is-active' : ''}`}
                onClick={() => onRoleChange(key)}
              >
                <span>{label}</span>
                <span className="ut-rail-option-count">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {departments.length > 0 && (
        <div className="ut-rail-section">
          <div className="ut-rail-label">Department</div>
          <div className="ut-rail-options">
            {departments.map((d) => {
              const active = deptFilterIds.includes(d.id);
              const count = deptCounts[d.id] || 0;
              return (
                <button
                  key={d.id}
                  type="button"
                  aria-pressed={active}
                  className={`ut-rail-option ${active ? 'is-active' : ''}`}
                  onClick={() => onDeptToggle(d.id)}
                >
                  <span>{d.name}</span>
                  <span className="ut-rail-option-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {anyFilterActive && (
        <button type="button" className="ut-rail-clear" onClick={onClearAll}>
          Clear all filters
        </button>
      )}
    </aside>
  );
}
